import React, { useState, useMemo } from "react";
import UniversalDataInspector from "../../../common/parsers/UniversalDataInspector";
import { SaveESG_taxonomyController } from "./controllers/SaveESG_taxonomyController";

export default function WorkflowValidationView({
  selectedFile,
  results,
  isProcessing,
  onValidate,
  onProceed
}) {
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [showJson, setShowJson] = useState(false);
  
  // --- AUDIT CONTROLLER STATE ---
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // 1. DYNAMIC SEARCH & FILTER ENGINE (The "Internal Controller")
  const auditResults = useMemo(() => {
    if (!results?.esgTree?.fullPot) return null;

    // Filter A: Search Text (Chapter/Label)
    let filtered = results.esgTree.fullPot;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(n => 
        (n.business_chapter?.toLowerCase().includes(q)) || 
        (n.label?.toLowerCase().includes(q))
      );
    }

    // Filter B: Role ID (Narrowed by search results)
    const availableRoles = [...new Set(filtered.map(n => n.role_id))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    if (roleFilter) {
      filtered = filtered.filter(n => n.role_id === roleFilter);
    }

    // C. Structural Stats
    const chapters = [...new Set(filtered.map(n => n.business_chapter))];
    const levels = [...new Set(filtered.map(n => n.level))].sort((a,b) => a-b);
    const levelCounts = filtered.reduce((acc, n) => {
      acc[n.level] = (acc[n.level] || 0) + 1;
      return acc;
    }, {});

    // D. Pagination
    const totalRows = filtered.length;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedRows = filtered.slice(startIndex, startIndex + rowsPerPage);

    return {
      paginatedRows,
      totalRows,
      availableRoles,
      chapters,
      maxLevel: levels.length > 0 ? levels[levels.length - 1] : 0,
      levelCounts
    };
  }, [results, searchQuery, roleFilter, currentPage]);

  const handleGenerateTree = () => {
    if (!results?.esgTree) {
      alert("Validation data not found. Please run 'START VALIDATION' first.");
      return;
    }
    setShowJson(true);
  };

  const handleFinalSave = async () => {
    try {
        const siteKey = process.env.REACT_APP_SITE_KEY;
        const response = await SaveESG_taxonomyController.promoteToCleanRoute(
          results.esgTree.fullPot, 
          siteKey
        );

        if (response.success) {
            alert(`Taxonomy Promoted: Batch #${response.batchId}`);
            if (onProceed) onProceed();
        }
    } catch (err) {
        alert("Save Failed: " + err.message);
    }
  };

  if (!selectedFile) return (
    <div className="p-5 text-center text-muted small italic bg-light rounded">
      Select an asset from the inventory to begin structural analysis.
    </div>
  );

  return (
    <div className="card shadow-sm border-0 bg-white overflow-hidden animate__animated animate__fadeIn">
      <div className="card-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold text-primary small">Structural Analysis</h6>
        <span className="badge bg-dark font-monospace" style={{ fontSize: "10px" }}>{selectedFile?.filename}</span>
      </div>

      <div className="card-body p-0">
        {!results ? (
          <div className="text-center py-5">
            <button className="btn btn-primary btn-sm fw-bold px-5" onClick={onValidate} disabled={isProcessing}>
              {isProcessing ? "ANALYZING..." : "START VALIDATION"}
            </button>
          </div>
        ) : (
          <div className="animate__animated animate__fadeIn">
            <div className="worksheet-list">
              {results.worksheets?.map((ws, i) => (
                <div key={i} className="border-bottom">
                  <div className={`d-flex align-items-center px-3 py-1 cursor-pointer ${expandedSheet === ws.name ? "bg-light" : ""}`}
                    onClick={() => setExpandedSheet(expandedSheet === ws.name ? null : ws.name)}>
                    <div className="flex-grow-1 small fw-bold">{ws.name}</div>
                    <div className="text-muted extra-small">{ws.rowCount} Rows</div>
                  </div>
                  {expandedSheet === ws.name && (
                    <div className="p-2 border-top bg-white"><UniversalDataInspector worksheet={ws} /></div>
                  )}
                </div>
              ))}
            </div>

            {!showJson && (
              <div className="p-3 bg-white border-top d-grid">
                <button className="btn btn-primary fw-bold" onClick={handleGenerateTree}>GENERATE ESG TREE</button>
              </div>
            )}

            {showJson && auditResults && (
              <div className="p-3 bg-light border-top animate__animated animate__fadeIn">
                
                {/* --- INTEGRITY AUDIT WIDGET --- */}
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-dark text-white py-2 d-flex justify-content-between">
                    <h6 className="mb-0 extra-small fw-bold text-uppercase">Taxonomy Integrity Audit</h6>
                    <span className="extra-small text-success fw-bold">{auditResults.totalRows} Matches</span>
                  </div>
                  <div className="card-body p-3 bg-white">
                    <div className="row g-2 mb-3">
                      <div className="col-md-7">
                        <label className="extra-small fw-bold text-muted text-uppercase">1. Search Chapter/Label</label>
                        <input type="text" className="form-control form-control-sm shadow-none" 
                          placeholder="Deep search strings..." value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} />
                      </div>
                      <div className="col-md-5">
                        <label className="extra-small fw-bold text-muted text-uppercase">2. Refine by Role</label>
                        <select className="form-select form-select-sm fw-bold shadow-none" 
                          value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}>
                          <option value="">All Roles ({auditResults.availableRoles.length})</option>
                          {auditResults.availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-4"><div className="border rounded p-1 text-center bg-light">
                        <div className="extra-small text-muted fw-bold">CHAPTERS</div>
                        <div className="fw-bold small">{auditResults.chapters.length}</div>
                      </div></div>
                      <div className="col-4"><div className="border rounded p-1 text-center bg-light">
                        <div className="extra-small text-muted fw-bold">MAX DEPTH</div>
                        <div className="fw-bold small">Lvl {auditResults.maxLevel}</div>
                      </div></div>
                      <div className="col-4"><div className="border rounded p-1 text-center bg-light">
                        <div className="extra-small text-muted fw-bold">MATCHES</div>
                        <div className="fw-bold small">{auditResults.totalRows}</div>
                      </div></div>
                    </div>

                    {/* PAGINATED AUDIT LIST */}
                    <div className="border rounded bg-dark p-2" style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <table className="table table-dark table-sm extra-small mb-0 font-monospace">
                        <thead>
                          <tr className="text-muted border-bottom border-secondary">
                            <th style={{width: '30px'}}>Lvl</th>
                            <th>Role</th>
                            <th>Business Chapter / Label</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditResults.paginatedRows.map((n, i) => (
                            <tr key={i} className="border-bottom border-secondary border-opacity-25">
                              <td className="text-info">{n.level}</td>
                              <td className="text-warning" style={{fontSize: '9px'}}>{n.role_id}</td>
                              <td>
                                <div className="text-truncate text-white" style={{ maxWidth: "350px" }}>{n.business_chapter}</div>
                                <div className="text-success" style={{fontSize: '9px'}}>{n.label}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 px-1">
                      <span className="extra-small text-muted italic">Page {currentPage} of {Math.ceil(auditResults.totalRows / rowsPerPage) || 1}</span>
                      <div className="btn-group">
                        <button className="btn btn-xs btn-outline-dark fw-bold" disabled={currentPage === 1} 
                          onClick={() => setCurrentPage(p => p - 1)}>PREV</button>
                        <button className="btn btn-xs btn-outline-dark fw-bold" disabled={currentPage * rowsPerPage >= auditResults.totalRows} 
                          onClick={() => setCurrentPage(p => p + 1)}>NEXT</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded border bg-dark p-3 mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  <pre className="text-success mb-0" style={{ fontSize: "10px", lineHeight: "1.1" }}>
                    {JSON.stringify(results.esgTree.fullPot, null, 2)}
                  </pre>
                </div>

                <div className="d-grid">
                  <button className="btn btn-success btn-lg fw-bold shadow" onClick={handleFinalSave}>
                    SAVE & CONCLUDE WORKFLOW <i className="bi bi-cloud-check ms-2"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}